/**
 * Rich Collection Picker Web Component
 *
 * Renders an accessible custom listbox dropdown where each option has a bold
 * title line and an optional subtitle (from collection.metafields.custom.subtitle).
 *
 * The closed/trigger state is visually identical to the native <select> collection
 * picker — both use the same .search-launcher-filter__select class and caret icon.
 *
 * Intentionally extends HTMLElement rather than Component so that
 * on:change="/handleCollectionChange" on the hidden input resolves through the
 * Component event delegation system to the ancestor search-launcher-component,
 * not to this element.
 *
 * Positioning: uses beforetoggle on the popover to position the panel directly
 * below the trigger button using viewport-relative coordinates (getBoundingClientRect
 * + position:fixed — the popover top-layer uses fixed positioning).
 *
 * Scroll tracking: while the panel is open, a capturing scroll listener continuously
 * repositions the panel so it stays anchored to the trigger during page scroll.
 *
 * Mega menu integration: listens for MegaMenuHoverEvent on the document and closes
 * the panel so it never renders above the mega menu dropdown contents.
 */
import { ThemeEvents } from '@theme/events';

class RichCollectionPickerComponent extends HTMLElement {
    /** @type {HTMLButtonElement | null} */
    #trigger = null;

    /** @type {HTMLElement | null} */
    #panel = null;

    /** @type {HTMLInputElement | null} */
    #valueInput = null;

    /** @type {HTMLElement | null} */
    #triggerLabel = null;

    /** @type {HTMLElement | null} */
    #listbox = null;

    /** @type {HTMLElement | null} */
    #focusedOption = null;

    /** @type {(() => void) | null} */
    #cleanupListeners = null;

    /** @type {(() => void) | null} */
    #scrollCleanup = null;

    connectedCallback() {
        this.#trigger = this.querySelector('.search-launcher-filter__select--rich');
        this.#panel = this.querySelector('.rich-collection-picker__panel');
        this.#valueInput = this.querySelector('.rich-collection-picker__value');
        this.#triggerLabel = this.querySelector('.rich-collection-picker__trigger-label');
        this.#listbox = this.querySelector('.rich-collection-picker__list');

        if (!this.#trigger || !this.#panel || !this.#listbox) return;

        const panel = /** @type {HTMLElement & { hidePopover(): void; showPopover(): void }} */ (this.#panel);
        const listbox = this.#listbox;
        const trigger = this.#trigger;

        const onBeforeToggle = (/** @type {ToggleEvent} */ event) => {
            if (event.newState === 'open') {
                this.#positionPanel();
                trigger.setAttribute('aria-expanded', 'true');
                const selected = listbox.querySelector('[aria-selected="true"]');
                if (selected) this.#setFocusedOption(/** @type {HTMLElement} */(selected));
                // Close the panel on page scroll or resize so there is never a position gap.
                // Panel-internal scroll (overflow-y: auto) is excluded.
                const panelRef = this.#panel;
                const onScroll = (/** @type {Event} */ scrollEvent) => {
                    if (panelRef && panelRef.contains(/** @type {Node} */(scrollEvent.target))) return;
                    /** @type {HTMLElement & { hidePopover(): void }} */ (panelRef).hidePopover();
                };
                window.addEventListener('scroll', onScroll, { passive: true, capture: true });
                window.addEventListener('resize', onScroll, { passive: true });
                this.#scrollCleanup = () => {
                    window.removeEventListener('scroll', onScroll, { capture: true });
                    window.removeEventListener('resize', onScroll);
                };
            } else {
                trigger.setAttribute('aria-expanded', 'false');
                this.#focusedOption = null;
                listbox.querySelectorAll('.is-focused').forEach((el) => el.classList.remove('is-focused'));
                this.#scrollCleanup?.();
                this.#scrollCleanup = null;
            }
        };

        const onListboxClick = (/** @type {MouseEvent} */ event) => {
            const option = /** @type {Element} */ (event.target).closest('[role="option"]');
            if (option) this.#selectOption(/** @type {HTMLElement} */(option));
        };

        const onTriggerKeydown = (/** @type {KeyboardEvent} */ event) => this.#handleKeydown(event);
        const onPanelKeydown = (/** @type {KeyboardEvent} */ event) => this.#handleKeydown(event);

        panel.addEventListener('beforetoggle', onBeforeToggle);
        listbox.addEventListener('click', onListboxClick);
        trigger.addEventListener('keydown', onTriggerKeydown);
        panel.addEventListener('keydown', onPanelKeydown);

        // Close the popover when the mega menu activates so the panel never
        // renders above the mega menu dropdown contents. hidePopover() is a
        // no-op when the panel is already closed — no guard required.
        const onMegaMenuHover = () => {
            /** @type {HTMLElement & { hidePopover(): void }} */ (panel).hidePopover();
        };
        document.addEventListener(ThemeEvents.megaMenuHover, onMegaMenuHover);

        this.#cleanupListeners = () => {
            panel.removeEventListener('beforetoggle', onBeforeToggle);
            listbox.removeEventListener('click', onListboxClick);
            trigger.removeEventListener('keydown', onTriggerKeydown);
            panel.removeEventListener('keydown', onPanelKeydown);
            document.removeEventListener(ThemeEvents.megaMenuHover, onMegaMenuHover);
        };
    }

    disconnectedCallback() {
        this.#cleanupListeners?.();
        this.#cleanupListeners = null;
        this.#scrollCleanup?.();
        this.#scrollCleanup = null;
    }

    #positionPanel() {
        if (!this.#trigger || !this.#panel) return;
        const rect = this.#trigger.getBoundingClientRect();
        const panel = /** @type {HTMLElement} */ (this.#panel);

        // The popover top-layer uses position:fixed — coordinates are viewport-relative.
        // No scroll offset needed; getBoundingClientRect() is already viewport-relative.
        panel.style.top = `${rect.bottom}px`;
        panel.style.left = `${rect.left}px`;
        panel.style.width = `${Math.floor(rect.width)}px`;

        // Clamp right edge to viewport
        const rightEdge = rect.left + panel.offsetWidth;
        if (rightEdge > window.innerWidth) {
            panel.style.left = `${Math.max(0, window.innerWidth - panel.offsetWidth)}px`;
        }
    }

    /**
     * Selects an option: updates the hidden input, trigger label, and aria state,
     * closes the panel, then dispatches a bubbling change event on the hidden input
     * so SearchLauncherComponent.handleCollectionChange() is called via Component
     * event delegation.
     *
     * @param {HTMLElement} option
     */
    #selectOption(option) {
        if (!this.#listbox || !this.#valueInput || !this.#triggerLabel) return;

        const value = option.dataset.value ?? '';
        const label = option.dataset.label ?? option.querySelector('.rich-collection-picker__option-title')?.textContent?.trim() ?? '';

        // Update hidden input value (SearchLauncherComponent reads this via .value)
        this.#valueInput.value = value;

        // Update trigger label to show selected collection
        this.#triggerLabel.textContent = label;

        // Update aria-selected on all options
        for (const opt of this.#listbox.querySelectorAll('[role="option"]')) {
            const isSelected = opt === option;
            opt.setAttribute('aria-selected', String(isSelected));
            opt.classList.toggle('rich-collection-picker__option--selected', isSelected);
        }

    // Close the popover panel
    /** @type {HTMLElement & { hidePopover(): void }} */ (this.#panel)?.hidePopover();

        // Dispatch a bubbling change event on the hidden input.
        // The Component event delegation system (component.js) catches this via:
        //   on:change="/handleCollectionChange" -> getClosestComponent() traverses
        //   past this HTMLElement to reach search-launcher-component.
        this.#valueInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * @param {KeyboardEvent} event
     */
    #handleKeydown(event) {
        if (!this.#panel || !this.#listbox) return;

        const isOpen = this.#panel.matches(':popover-open');
        const options = /** @type {NodeListOf<HTMLElement>} */ (this.#listbox.querySelectorAll('[role="option"]'));
        const currentIdx = this.#focusedOption ? Array.from(options).indexOf(this.#focusedOption) : -1;

        if (!isOpen) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
        /** @type {HTMLElement & { showPopover(): void }} */ (this.#panel).showPopover();
            }
            return;
        }

        let option = null;

        switch (event.key) {
            case 'Escape':
                event.preventDefault();
        /** @type {HTMLElement & { hidePopover(): void }} */ (this.#panel).hidePopover();
                this.#trigger?.focus();
                break;
            case 'ArrowDown':
                event.preventDefault();
                option = options[Math.min(currentIdx + 1, options.length - 1)];

                if (option) {
                    this.#setFocusedOption(option);
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                option = options[Math.max(currentIdx - 1, 0)];

                if (option) {
                    this.#setFocusedOption(option);
                }

                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                if (this.#focusedOption) this.#selectOption(this.#focusedOption);
                break;
        }
    }

    /**
     * @param {HTMLElement} option
     */
    #setFocusedOption(option) {
        if (this.#focusedOption) this.#focusedOption.classList.remove('is-focused');
        this.#focusedOption = option;
        option.classList.add('is-focused');
        option.scrollIntoView({ block: 'nearest' });
    }
}

customElements.define('rich-collection-picker', RichCollectionPickerComponent);
